<script setup lang="ts">
import type { SearchFormProperties } from '@optionway/models';
import { toCacheKey } from '@pinia/colada';
import { breakpointsTailwind } from '@vueuse/core';
import { ArrowLeftRight } from 'lucide-vue-next';
import { z } from 'zod';

const prefix = 'components.search.form';

const searchCriteriaStore = useSearchCriteriaStore();
const searchResultIndexStore = useSearchResultIndexStore();
const searchResultFiltersStore = useSearchResultFiltersStore();
const gtmStore = useGtmStore();

const route = useRoute();
const localePath = useLocalePath();
// Route name, not path: with the prefix_and_default strategy the default locale has both /results and /fr/results.
const routeBaseName = useRouteBaseName();
const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.searchForm;
const searchFormTypeSelectorConfig = $appConfig.app?.components?.searchFormTypeSelector;

const properties = defineProps<SearchFormProperties & { compact?: boolean; autoFocus?: boolean }>();
const emit = defineEmits<{ 'update:autoFocus': [value: boolean]; search: [] }>();

const componentConfig = computed<SearchFormProperties>(() => {
  return {
    components: properties.components ?? config?.components,
    hideComponents: properties.hideComponents ?? config?.hideComponents,
    minDaysBeforeDeparture: properties.minDaysBeforeDeparture ?? config?.minDaysBeforeDeparture,
  };
});

const { invalidateQueries } = useQueryCache();
const { loading: queryLoading } = useSearchResultsQueryState();
const searching = ref(false);
const loading = computed(() => searching.value || queryLoading.value);

const schema = z
  .object({
    from: z.string().min(1, `${prefix}.errors.origin`),
    to: z.string().min(1, `${prefix}.errors.destination`),
    departureDate: z
      .string()
      .min(1, `${prefix}.errors.departureDate`)
      .refine(
        (date) => isSearchDepartureDateAllowed(date, componentConfig.value.minDaysBeforeDeparture),
        `${prefix}.errors.departureDateTooSoon`,
      )
      .refine(isSearchDepartureDateNotTooFar, `${prefix}.errors.departureDateTooFar`),
    returnDate: z.string().min(1, `${prefix}.errors.returnDate`),
  })
  .refine((data) => data.from !== data.to, {
    message: `${prefix}.errors.sameCity`,
    path: ['to'],
  });

const submitted = ref(false);

const errors = computed(() => {
  if (!submitted.value) return {};
  const result = schema.safeParse({
    from: searchCriteriaStore.searchRequest.legs[0]?.from?.code ?? '',
    to: searchCriteriaStore.searchRequest.legs[0]?.to?.code ?? '',
    departureDate: searchCriteriaStore.departureDate ?? '',
    returnDate: searchCriteriaStore.isOneWay || searchCriteriaStore.returnDate ? 'filled' : '',
  });
  if (result.success) return {};
  const { fieldErrors } = result.error.flatten();
  return {
    from: fieldErrors.from?.[0],
    to: fieldErrors.to?.[0],
    departureDate: fieldErrors.departureDate?.[0],
    returnDate: fieldErrors.returnDate?.[0],
  };
});

const search = async (keepFilters = false) => {
  submitted.value = true;
  if (errors.value.from || errors.value.to || errors.value.departureDate || errors.value.returnDate) {
    return;
  }
  emit('search');
  // Freeze the current result count so the skeleton loader shows the same number of rows
  // as the previous search while the new results are loading.
  searchResultIndexStore.snapshotCount();
  // Disarm filtering before invalidation — resetFilters() sets active=false so the
  // query does not immediately refetch when its cache entry is dropped below.
  // keepFilters is true only for the deep-link auto-launch, where the entry-point
  // middleware has already applied filters (e.g. stops=0) from the URL query.
  if (!keepFilters) {
    searchResultFiltersStore.resetFilters();
  }
  // Sync round-trip legs before snapshotCriteriaKey — legs[1].from/to must match legs[0].to/from
  // so the snapshot captures the correct, consistent key.
  searchCriteriaStore.syncRoundTripLegs();
  searching.value = true;
  try {
    // Disarm the main query before invalidation to prevent an immediate eager refetch.
    searchCriteriaStore.active = false;
    // Drop both main and filtering cache entries under the OLD snapshot key before overwriting it.
    // Both queries are now disabled, so invalidation does not trigger any fetch here.
    // The filtering cache (staleTime: Infinity) must be explicitly dropped so that
    // a repeated same-filter combination after this search re-fetches fresh data.
    if (searchCriteriaStore.searchedCriteriaKey) {
      await invalidateQueries({
        key: ['search-results', toCacheKey([searchCriteriaStore.searchedCriteriaKey])],
        exact: false,
      });
    }
    // Snapshot the new criteria key so getEntries in search-result-index always reads from the last-searched cache.
    searchCriteriaStore.snapshotCriteriaKey();
    gtmStore.trackSearch(keepFilters); // after the snapshot: the context reads the committed criteria
    // Skip navigation when already on results — same-route navigateTo is unreliable in Vue Router 5
    // (seen in production builds) and prevents active from being set in the deep-link case.
    if (routeBaseName(route) !== 'results') {
      await navigateTo(localePath('results'));
    }
    // Re-arm: pinia-colada sees enabled=true + no cache for this key → fires exactly one fetch.
    // list.vue resets active back to false once the query settles.
    searchCriteriaStore.active = true;
  } finally {
    searching.value = false;
  }
};

const breakpoints = useBreakpoints(breakpointsTailwind);
// md, not the calendar's lg: the guided flow is only reachable through the autocomplete's mobile
// sheet, whose trigger is md:hidden. Above md the fields are desktop comboboxes and never emit
// `select` — the chain has no entry point there, whatever the calendar renders.
const isMobile = breakpoints.smallerOrEqual('md');

const destinationSheetOpen = ref(false);
const autoOpenDeparture = ref(false);

const onOriginSelect = async () => {
  if (!isMobile.value || searchCriteriaStore.searchRequest.legs[0]?.to?.code) {
    return;
  }
  // The origin sheet closes itself just before emitting `select`, but that close is not applied yet
  // when we get here. Wait one tick, otherwise the destination sheet opens on top of a sheet that is
  // still on screen and both fight over the focus trap.
  await nextTick();
  destinationSheetOpen.value = true;
};

const onDestinationSelect = async () => {
  if (isMobile.value && !searchCriteriaStore.departureDate) {
    // Same as onOriginSelect: wait for the destination sheet to be really closed before raising the
    // flag, otherwise the calendar opens in the same frame and the two overlap.
    await nextTick();
    autoOpenDeparture.value = true;
  }
};

const isShiftPressed = useKeyModifier('Shift', { events: ['keydown', 'keyup'] });

const focusFromInput = ref(false);

watch(
  () => properties.autoFocus,
  (value) => {
    if (value) {
      focusFromInput.value = true;
      emit('update:autoFocus', false);
    }
  },
);

onMounted(async () => {
  // fromDeepLink: deep-link auto-launch. refreshRequested: offer-expiration refresh — results.vue mounts this
  // form on either flag.
  if (!searchCriteriaStore.fromDeepLink && !searchCriteriaStore.refreshRequested) {
    return;
  }
  // Disarm BOTH on ANY page: a deep-link to home must not leave fromDeepLink dangling, otherwise it re-fires
  // a redundant search on the next results visit.
  searchCriteriaStore.fromDeepLink = false;
  searchCriteriaStore.refreshRequested = false;
  // Only auto-search when actually on results. search() validates the first and last legs only: a multi-destination
  // leg the URL left incomplete leaves the form to the traveller instead of reaching the API.
  const isIncompleteMultiDestination =
    searchCriteriaStore.isMultiDestination && !searchCriteriaStore.hasRequiredCriteria;
  if (routeBaseName(route) === 'results' && !isIncompleteMultiDestination) {
    await search(true);
  }
});

const searchButtonReference = useTemplateRef('searchButtonReference');
const { focused: searchButtonFocused } = useFocus(searchButtonReference);

const passengersTypesFocused = ref(false);

const onRangeSelected = () => {
  if (isMobile.value) {
    passengersTypesFocused.value = true;
  }
};

const onPopoverClosed = () => {
  if (isShiftPressed.value) {
    passengersTypesFocused.value = true;
  } else {
    searchButtonFocused.value = true;
  }
};
</script>

<template>
  <div
    v-if="!properties.compact"
    class="flex flex-col pb-3 md:flex-row md:flex-wrap md:items-center"
  >
    <SearchFormTypeSelector
      v-if="!componentConfig.hideComponents?.searchFormTypeSelector"
      :model-value="searchCriteriaStore.searchRequest.tripType"
      :ignored-trip-type="searchFormTypeSelectorConfig?.ignoredTripType"
      @update:model-value="searchCriteriaStore.setTripType"
    />

    <div class="hidden px-3.5 md:block">
      <SearchCabinClassSelector
        v-if="!componentConfig.hideComponents?.searchCabinClassSelector"
        v-model="searchCriteriaStore.searchRequest.cabinClass"
      />
    </div>
  </div>

  <UiCustomAnimatedHeight>
    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0 translate-y-2"
      leave-active-class="transition-all duration-150 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0"
    >
      <!-- ROUND_TRIP -->
      <div
        v-if="
          (searchCriteriaStore.isRoundTrip || searchCriteriaStore.isOneWay) && searchCriteriaStore.searchRequest.legs[0]
        "
        :class="[
          'flex w-full flex-col gap-3',
          properties.compact
            ? 'md:flex-row md:items-start md:gap-2'
            : 'xl:grid xl:grid-cols-search-form xl:items-start',
        ]"
      >
        <div
          :class="[
            'relative flex w-full flex-col gap-2 md:flex-row md:items-start',
            properties.compact ? 'md:flex-1 md:gap-1.5' : 'xl:min-w-0',
          ]"
        >
          <SearchAutocompleteInput
            v-if="!componentConfig.hideComponents?.searchAutocompleteInput"
            v-model="searchCriteriaStore.searchRequest.legs[0].from"
            v-model:auto-focus="focusFromInput"
            :label="$t(`${prefix}.origin`)"
            :error="errors.from ? $t(errors.from) : undefined"
            :compact="properties.compact"
            field="from"
            :leg-index="0"
            @select="onOriginSelect"
          />

          <UiButton
            type="button"
            :aria-label="$t(`${prefix}.reverseTrip`)"
            :class="[
              'absolute -right-2 bottom-0 top-0 z-10 m-auto w-11 shrink-0 rotate-90 rounded-lg border border-gray-500 bg-surface p-2 shadow-sm hover:bg-surface md:static md:m-0 md:rotate-0',
              properties.compact ? 'h-9 md:h-9 md:w-9' : 'h-11 xl:mt-6',
            ]"
            @click="searchCriteriaStore.reverseLeg(0)"
          >
            <ArrowLeftRight class="text-gray-500" />
          </UiButton>

          <SearchAutocompleteInput
            v-if="!componentConfig.hideComponents?.searchAutocompleteInput"
            v-model="searchCriteriaStore.searchRequest.legs[0].to"
            v-model:sheet-open="destinationSheetOpen"
            :label="$t(`${prefix}.destination`)"
            :error="errors.to ? $t(errors.to) : undefined"
            :compact="properties.compact"
            field="to"
            :leg-index="0"
            @select="onDestinationSelect"
          />
        </div>

        <div
          v-if="!componentConfig.hideComponents?.searchDatepicker"
          :class="properties.compact ? 'md:flex md:min-w-0 md:flex-1 md:flex-col md:justify-end' : 'w-full xl:min-w-0'"
        >
          <SearchDatepicker
            v-model:departure-date="searchCriteriaStore.searchRequest.legs[0].departureDate"
            v-model:auto-open="autoOpenDeparture"
            :return-date="searchCriteriaStore.returnDate"
            :departure-error="
              errors.departureDate ? $t(errors.departureDate, componentConfig.minDaysBeforeDeparture ?? 0) : undefined
            "
            :return-error="errors.returnDate ? $t(errors.returnDate) : undefined"
            :compact="properties.compact"
            :one-way="searchCriteriaStore.isOneWay"
            :origin="searchCriteriaStore.originCode"
            :destination="searchCriteriaStore.destinationCode"
            @update:return-date="searchCriteriaStore.setReturnDate"
            @range-selected="onRangeSelected"
          />
        </div>

        <div :class="['flex w-full flex-col gap-1', properties.compact ? 'md:w-44' : '']">
          <span
            v-if="!properties.compact"
            class="hidden text-sm font-medium text-text-secondary-600 xl:flex"
          >
            {{ $t(`${prefix}.paxTypologyTitle`) }}
          </span>

          <SearchPassengersTypes
            v-if="!componentConfig.hideComponents?.searchPassengersTypes"
            v-model:cabin-class="searchCriteriaStore.searchRequest.cabinClass"
            v-model:active="passengersTypesFocused"
            :adults="searchCriteriaStore.searchRequest.adults"
            :children="searchCriteriaStore.searchRequest.children"
            :infants="searchCriteriaStore.searchRequest.infants"
            :compact="properties.compact"
            @update:adults="searchCriteriaStore.updateAdults"
            @update:children="searchCriteriaStore.updateChildren"
            @update:infants="searchCriteriaStore.updateInfants"
            @popover-closed="onPopoverClosed"
          />
        </div>

        <UiButton
          ref="searchButtonReference"
          variant="primary"
          color="brand-primary"
          :loading="loading"
          :loading-label="$t('components.common.loading')"
          :disabled="routeBaseName(route) === 'results' && !searchCriteriaStore.hasUncommittedCriteria"
          :class="['w-full rounded-lg', properties.compact ? 'h-9 px-4 md:w-40' : 'h-11 xl:mt-6']"
          @click="() => search()"
        >
          {{ routeBaseName(route) === 'results' ? $t(`${prefix}.actualize`) : $t(`${prefix}.search`) }}
        </UiButton>
      </div>

      <SearchMultidestForm
        v-else-if="!componentConfig.hideComponents?.searchMultidestForm"
        :loading="loading"
        @search="() => search()"
      />
    </Transition>
  </UiCustomAnimatedHeight>
</template>
